// «الجرد اليومي» — small shared helpers for every daily-count screen. The
// formatting / error helpers are the stock count's (imported, never copied),
// so both modules render numbers, money, months and server errors alike.
import api from "@/lib/api";
import { wmError } from "@/lib/wmError";
import { shiftDate } from "@/components/cancellations/coUtils";

export {
    SC_TIMEOUT_MS as DK_TIMEOUT_MS, SC_LONG_TIMEOUT_MS as DK_LONG_TIMEOUT_MS,
    scError as dkError, scMessage as dkMessage,
    fmt, labelOf, fmtDateTime, fmtMoney, fmtQuantity, fmtPeriod, fmtDay,
    sanitizeQuantity, isCounted, blobErrorMessage,
} from "@/components/stock-count/scUtils";
export { shiftDate };

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/;
export const isIsoDate = (v) => typeof v === "string" && DATE_RE.test(v);
export const isIsoMonth = (v) => typeof v === "string" && MONTH_RE.test(v);

/** "2026-11" -> "2026-10" (n = -1). */
export function addMonths(month, n) {
    const y = Number(month.slice(0, 4));
    const m = Number(month.slice(5, 7)) - 1 + n;
    const yy = y + Math.floor(m / 12);
    const mm = ((m % 12) + 12) % 12;
    return `${yy}-${String(mm + 1).padStart(2, "0")}`;
}

/** The last `n` months, newest first, ending at `latest` ("YYYY-MM"). */
export function monthsBack(latest, n = 12) {
    if (!isIsoMonth(latest)) return [];
    return Array.from({ length: n }, (_, i) => addMonths(latest, -i));
}

/** "2026-11-01" -> «الأحد ١ نوفمبر» / "Sun 1 Nov". */
export function fmtWeekday(iso, lang) {
    if (!isIsoDate(iso)) return iso || "";
    const [y, m, d] = iso.split("-").map(Number);
    try {
        return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA" : "en-GB", {
            weekday: "short", day: "numeric", month: "short", calendar: "gregory", timeZone: "UTC",
        }).format(new Date(Date.UTC(y, m - 1, d)));
    } catch {
        return iso;
    }
}

const nf = (lang, opts) => new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US", opts);

/** A signed quantity: «+٢٫٥» / «−١» / «٠»; null -> "". */
export function fmtSigned(q, lang) {
    if (q === null || q === undefined || q === "" || Number.isNaN(Number(q))) return "";
    const n = Math.round(Number(q) * 1000) / 1000;
    const s = nf(lang, { maximumFractionDigits: 3 }).format(Math.abs(n));
    if (n > 0) return `+${s}`;
    if (n < 0) return `−${s}`;
    return s;
}

/** A percentage number (5 = 5 %): «+٣٫٨٪» / "+3.8%"; null -> "". */
export function fmtPct(pct, lang) {
    if (pct === null || pct === undefined || Number.isNaN(Number(pct))) return "";
    const n = Number(pct);
    const s = nf(lang, { style: "percent", maximumFractionDigits: 1 }).format(Math.abs(n) / 100);
    if (n > 0) return `+${s}`;
    if (n < 0) return `−${s}`;
    return s;
}

/** % of theoretical, computed here so it never depends on how the server scales `pct`. */
export function pctOf(unexplained, theoretical, serverPct) {
    const u = Number(unexplained);
    const th = Number(theoretical);
    if (unexplained !== null && unexplained !== undefined && theoretical !== null && theoretical !== undefined
        && !Number.isNaN(u) && th > 0) {
        return (u / th) * 100;
    }
    return serverPct === null || serverPct === undefined ? null : Number(serverPct);
}

/** The flag rule of SPEC §3.3, for an UNSAVED line's preview (the server decides on save). */
export function flagOf({ unexplained, theoretical, unitClass, itemMinMilli, settings }) {
    if (theoretical === null || theoretical === undefined || unexplained === null || unexplained === undefined) return false;
    const pct = Number(settings?.flag_pct ?? 5);
    const defMilli = unitClass === "pc" ? settings?.flag_min_pc_milli ?? 12000 : settings?.flag_min_kg_milli ?? 1000;
    const min = Number(itemMinMilli ?? defMilli) / 1000;
    const u = Math.abs(Number(unexplained));
    return u > (pct / 100) * Math.abs(Number(theoretical)) && u >= min;
}

/** The count unit in the UI language, by class: the server's `count_unit` is
 *  always the Arabic word («كيلو» / «حبة», identical to the ar labels), so it
 *  is only the fallback for an item without a known class. */
export const unitOf = (item, t) => {
    if (item?.unit_class === "pc") return t("dk_unit_pc");
    if (item?.unit_class === "kg") return t("dk_unit_kg");
    return item?.count_unit || t("dk_unit_kg");
};

/** Kilo items take decimals, piece items whole numbers (the server's `allows_decimal` wins). */
export const allowsDecimalOf = (item) => (
    item?.allows_decimal === undefined || item?.allows_decimal === null ? item?.unit_class !== "pc" : !!item.allows_decimal
);

/** Per-line errors of a `DK_BULK_INVALID` refusal, keyed by item id (a line may carry several). */
export function dkLineErrors(e, payloadLines) {
    const d = wmError(e);
    const out = {};
    for (const err of d?.errors || []) {
        const id = err.item_id || payloadLines?.[Number(err.line_index)]?.item_id;
        if (!id) continue;
        (out[id] = out[id] || []).push(err);
    }
    return out;
}

/** «تصدير Excel»: a blob download of `GET /dk/export`. Resolves the file name it used. */
export async function downloadDkExport(month, lang, timeout) {
    const r = await api.get("/dk/export", { params: { month, lang }, responseType: "blob", timeout });
    const name = (r.headers?.["content-disposition"] || "").match(/filename="?([^";]+)"?/)?.[1] || `daily_count_${month}.xlsx`;
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return name;
}

/** Every dk location a transfer may go to. A counter's `/dk/scope` lists only its
 *  own locations, so this prefers a server-given list (`scope.destinations` /
 *  `scope.all_locations`), then the stock count's location list (branches whose
 *  code is in the daily-count settings), then the scope's own locations. */
let scLocationsPromise = null;
export function loadDestinations(scope) {
    const given = scope?.destinations || scope?.all_locations;
    if (Array.isArray(given) && given.length) return Promise.resolve(given);
    const own = scope?.locations || [];
    const codes = new Set(scope?.settings?.location_codes || []);
    if (!scLocationsPromise) {
        scLocationsPromise = api.get("/sc/locations")
            .then((r) => (Array.isArray(r.data) ? r.data : r.data?.locations || []))
            .catch(() => null);
    }
    return scLocationsPromise.then((list) => {
        if (!list) {
            scLocationsPromise = null;   // a 403 / offline answer is not cached
            return own;
        }
        const branches = list.filter((l) => (l.kind || "branch") === "branch" && l.is_active !== false
            && (!codes.size || codes.has(l.code)));
        const byId = new Map(branches.map((l) => [l.id, l]));
        own.forEach((l) => { if (!byId.has(l.id)) byId.set(l.id, l); });
        return [...byId.values()];
    });
}

/** The reconcile rows as a flat list of {loc, item, ...fields}, whatever the
 *  server's grouping: a `rows` array of objects carrying `location_id` /
 *  `item_id`, or a nested `{location_id: {item_id: {...}}}` under `cells` / `rows`.
 *  An item the location does not count (`{excluded: true}`) is not a row. */
export function reconcileRows(data) {
    const out = [];
    const list = data?.rows;
    if (Array.isArray(list)) {
        for (const r of list) {
            const loc = r?.location_id ?? r?.location?.id;
            const item = r?.item_id ?? r?.item?.id;
            if (loc && item && !r.excluded) out.push({ ...r, loc, item });
        }
        return out;
    }
    const nested = data?.cells || (list && typeof list === "object" ? list : null) || data?.lines;
    for (const [loc, byItem] of Object.entries(nested || {})) {
        for (const [item, r] of Object.entries(byItem || {})) {
            if (r && typeof r === "object" && !r.excluded) out.push({ ...r, loc, item });
        }
    }
    return out;
}

/** The server's recipe match key (`consumption_routes._norm_key`): the theoretical
 *  matches item keys to recipe ingredient keys THROUGH it, so the admin screens
 *  compare keys the same way (a case / spacing difference is not a broken link). */
const AR_DIACRITICS = /[ً-ْ]/g;
export function normKey(s) {
    if (typeof s !== "string") return "";
    return s.trim().toLowerCase().replace(AR_DIACRITICS, "").replace(/[-_]/g, " ").replace(/\s+/g, " ").trim();
}

export const DK_REASON_MIN = 5;
